import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { message } from 'antd';
import { useParams } from 'react-router';
import TitleBar from '../../../components/TitleBar';
import {
  FETCH_SUPPLIERS,
  FIND_SUPPLIERS_BY_LOCATION,
  ASSIGN_SUPPLIERS_TO_LOCATION,
  UNASSIGN_SUPPLIERS_TO_LOCATION,
  CREATE_SUPPLIER_DEPENDENCIES,
  DELETE_SUPPLIER_DEPENDENCIES,
} from '../queries';
import { Footer } from '../../../components/common/Footer';
import {
  BorderedSection,
  Button,
  FilterHeader,
  Loading,
  SaveInstructions,
} from '../../../components';
import { useSearchParams } from 'react-router-dom';
import ListManager from '../../../components/common/ListManager';
import QueryErrorBoundary from '../../../components/error-handling';
import {
  FindAllSupplierByLocationResponse,
  FindAllSupplierResponse,
  Supplier,
} from '../../../interfaces/suppliers';
import { useUserContext } from '../../../context/PermissionsContext';
import TitleUpdater from '../../../components/common/TitleUpdater';

const SupplierMapping = () => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const locationName = searchParams.get('locationName');

  const [addedSuppliers, setAddedSuppliers] = useState<Supplier[]>([]);
  const [availableSuppliers, setAvailableSuppliers] = useState<Supplier[]>([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState<Supplier[]>([]);
  const [search, setSearch] = useState('');

  const {
    data: suppliers,
    loading: suppliersLoading,
    error: suppliersError,
  } = useQuery<FindAllSupplierResponse>(FETCH_SUPPLIERS);

  const {
    data: suppliersByLocation,
    loading: suppliersByLocationLoading,
    error: suppliersByLocationError,
  } = useQuery<FindAllSupplierByLocationResponse>(FIND_SUPPLIERS_BY_LOCATION, {
    variables: { locationUuid: params.locationId },
  });

  const loadingStates = suppliersLoading || suppliersByLocationLoading;

  const orderedAvailableSuppliers = [...filteredSuppliers].sort((a, b) => {
    return a.supplierName.localeCompare(b.supplierName);
  });

  const orderedAddedSuppliers = [...addedSuppliers].sort((a, b) => {
    return a.supplierName.localeCompare(b.supplierName);
  });

  const checkedSuppliers = suppliersByLocation?.findSupplierByLocation || [];
  const uncheckedSuppliers =
    suppliers?.findAllSupplier.filter(
      (supplier) =>
        !checkedSuppliers.some(
          (checkedSupplier) => checkedSupplier.uuid === supplier.uuid,
        ),
    ) || [];

  const { canEdit } = useUserContext();

  const [AssignSuppliersToLocationGQL] = useMutation(
    ASSIGN_SUPPLIERS_TO_LOCATION,
    {
      refetchQueries: ['FindAllSupplier', 'FindSupplierByLocation'],
    },
  );
  const [UnAssignSuppliersToLocationGQL] = useMutation(
    UNASSIGN_SUPPLIERS_TO_LOCATION,
    {
      refetchQueries: ['FindAllSupplier', 'FindSupplierByLocation'],
    },
  );
  const [CreateSupplierDependency, { loading: createLoading }] = useMutation(
    CREATE_SUPPLIER_DEPENDENCIES,
    {
      refetchQueries: ['FindAllSupplier', 'FindSupplierByLocation'],
    },
  );
  const [DeleteSupplierDependency, { loading: deleteLoading }] = useMutation(
    DELETE_SUPPLIER_DEPENDENCIES,
    {
      refetchQueries: ['FindAllSupplier', 'FindSupplierByLocation'],
    },
  );
  const [messageApi, contextHolder] = message.useMessage();

  const filterAvailableSuppliers = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase();
    setSearch(value);
    const filtered = availableSuppliers.filter((supplier) =>
      supplier.supplierName.toLowerCase().includes(value),
    );
    setFilteredSuppliers(filtered);
  };

  const onAddSupplier = (supplier: Supplier) => {
    setAddedSuppliers((prev) => [...prev, supplier]);
    setAvailableSuppliers((prev) =>
      prev.filter((item) => item.uuid !== supplier.uuid),
    );
    setFilteredSuppliers((prev) =>
      prev.filter((item) => item.uuid !== supplier.uuid),
    );
  };

  const onRemoveSupplier = (supplier: Supplier) => {
    setAvailableSuppliers((prev) => [...prev, supplier]);
    setFilteredSuppliers((prev) => [...prev, supplier]);
    setAddedSuppliers((prev) =>
      prev.filter((item) => item.uuid !== supplier.uuid),
    );
  };

  const onSaveChanges = async () => {
    if (!canEdit) {
      message.info('You do not have permission to edit this data');
      return;
    }

    const newSuppliersAdded = addedSuppliers.filter(
      (supplier) =>
        !checkedSuppliers.some(
          (checkedSupplier) => checkedSupplier.uuid === supplier.uuid,
        ),
    );

    const addedSuppliersIds = newSuppliersAdded.map(
      (supplier) => supplier.uuid,
    );

    const removedSuppliers = checkedSuppliers.filter(
      (supplier) =>
        !addedSuppliers.some(
          (addedSupplier) => addedSupplier.uuid === supplier.uuid,
        ),
    );

    const removedSuppliersIds = removedSuppliers.map(
      (supplier) => supplier.uuid,
    );

    try {
      if (addedSuppliersIds.length > 0) {
        await AssignSuppliersToLocationGQL({
          variables: {
            data: {
              locationUuid: params.locationId,
              supplierUuids: addedSuppliersIds,
            },
          },
        });

        await CreateSupplierDependency({
          variables: {
            input: {
              locationUuid: params.locationId,
              supplierUuids: addedSuppliersIds,
            },
          },
        });
      }

      if (removedSuppliersIds.length > 0) {
        await UnAssignSuppliersToLocationGQL({
          variables: {
            data: {
              locationUuid: params.locationId,
              supplierUuids: removedSuppliersIds,
            },
          },
        });

        await DeleteSupplierDependency({
          variables: {
            input: {
              locationUuid: params.locationId,
              supplierUuids: removedSuppliersIds,
            },
          },
        });
      }

      messageApi.open({
        type: 'success',
        content: 'Changes Saved',
      });
    } catch (error) {
      console.log(error);
      messageApi.open({
        type: 'error',
        content: 'Operation Failed',
      });
    }
  };

  useEffect(() => {
    if (!suppliers || !suppliersByLocation) {
      return;
    }

    setAddedSuppliers(checkedSuppliers);
    setAvailableSuppliers(uncheckedSuppliers);
    setFilteredSuppliers(uncheckedSuppliers);
  }, [suppliers, suppliersByLocation]);

  return (
    <>
      {contextHolder}
      <TitleUpdater title={`${locationName}/Supplier Selection`} />
      <TitleBar
        title="Dependency Mapping"
        subtitle={`Suppliers To ${locationName}`}
        brief={`Select which suppliers directly support the ${locationName} location. Exact percentages would be captured on the next page. If a supplier is missing, it needs to be added in the data inputs section.`}
      />

      <QueryErrorBoundary error={[suppliersError, suppliersByLocationError]}>
        <BorderedSection
          title=""
          noPadding
          headingClassName="h-20"
          contentClassName="py-4"
          extendOptions={
            <FilterHeader
              searchTerm={search}
              onSearch={filterAvailableSuppliers}
            />
          }
        >
          {!loadingStates ? (
            <ListManager
              addedItems={orderedAddedSuppliers}
              availableItems={orderedAvailableSuppliers}
              onRemoveItem={onRemoveSupplier}
              onAddItem={onAddSupplier}
              addedTitle="SUPPLIERS ADDED"
              availableTitle="SUPPLIERS AVAILABLE"
              getItemLabel={(item: Supplier) => item.supplierName}
              infoContent={<SaveInstructions isLocation={!!locationName} />}
            />
          ) : (
            <Loading />
          )}
        </BorderedSection>
      </QueryErrorBoundary>
      <div className="flex items-center justify-center mt-4">
        <Button
          onClick={onSaveChanges}
          type="primary"
          className="text-sm mt-4 min-w-[180px]"
          disabled={createLoading || deleteLoading}
        >
          Save
        </Button>
      </div>
      <Footer
        backPath={'/suppliers-dependencies'}
        nextPath={`/suppliers-dependencies/${params.locationId}/mapping?locationName=${locationName}`}
      />
    </>
  );
};

export default SupplierMapping;
